import { MessagesApplicationsRoleTemplateAssetPermissions } from '@/dotnet-client'
import {
  Button,
  createTableColumn,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  TableColumnDefinition,
} from '@fluentui/react-components'
import { Navigation24Regular } from '@fluentui/react-icons'
import { useEffect, useState } from 'react'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { DataGridComponent } from '../DataGrid/DataGridComponent'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import { ApplicationPermissionsForm } from '../Forms/ApplicationPermissionsForm'

type AppPermsPanelProps = {
  appId: number
}

export const AppPermsPanel = (props: AppPermsPanelProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [applicationPerms, setApplicationPerms] = useState<MessagesApplicationsRoleTemplateAssetPermissions[]>([])
  const [addAppPermDrawerOpen, setAddAppPermDrawerOpen] = useState<boolean>(false)
  const [editAppPermDrawerOpen, setEditAppPermDrawerOpen] = useState<boolean>(false)
  const [deleteAppPermDrawerOpen, setDeleteAppPermDrawerOpen] = useState<boolean>(false)
  const [selectedAppPerm, setSelectedAppPerm] = useState<MessagesApplicationsRoleTemplateAssetPermissions>()

  const fetchPermsList = async () => {
    dmpClient?.applicationsApi
      .getApplicationsApiGetRolePermissions(props.appId)
      .then((result) => {
        setApplicationPerms(result)
      })
      .catch((err) => {
        console.warn(err)
      })
      .finally(() => {})
  }

  useEffect(() => {
    if (isDMPClientLoaded) fetchPermsList()
  }, [isDMPClientLoaded])

  const columns: TableColumnDefinition<MessagesApplicationsRoleTemplateAssetPermissions>[] = [
    createTableColumn<MessagesApplicationsRoleTemplateAssetPermissions>({
      columnId: 'roleName',
      compare: (a, b) => {
        return a.roleName.localeCompare(b.roleName)
      },
      renderHeaderCell: () => {
        return 'Role'
      },
      renderCell: (item) => {
        return item.roleName
      },
    }),
    createTableColumn<MessagesApplicationsRoleTemplateAssetPermissions>({
      columnId: 'resource',
      compare: (a, b) => {
        return a.resource.localeCompare(b.resource)
      },
      renderHeaderCell: () => {
        return 'Config Type'
      },
      renderCell: (item) => {
        return item.resource == 'template' ? 'File Template' : 'Data Asset'
      },
    }),
    createTableColumn<MessagesApplicationsRoleTemplateAssetPermissions>({
      columnId: 'name',
      compare: (a, b) => {
        return a.name.localeCompare(b.name)
      },
      renderHeaderCell: () => {
        return 'Config Description'
      },
      renderCell: (item) => {
        return item.name
        //item.description
      },
    }),
    createTableColumn<MessagesApplicationsRoleTemplateAssetPermissions>({
      columnId: 'permissions',
      compare: (a, b) => {
        return a.description.localeCompare(b.description)
      },
      renderHeaderCell: () => {
        return 'Permissions'
      },
      renderCell: (item) => {
        const itemPerms = JSON.parse(item.permissions)
        const perms = Object.keys(itemPerms[0])
          .filter((x) => x.includes('allow') && itemPerms[0][x])
          .map((key) => {
            return `${key.replace(/_/g, ' ')}`
          })
          .join(', ')

        return <div className="capitalize">{perms}</div>
      },
    }),

    createTableColumn<MessagesApplicationsRoleTemplateAssetPermissions>({
      columnId: 'actions',
      renderHeaderCell: () => {
        return 'Actions'
      },
      renderCell: (item) => {
        return (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button icon={<Navigation24Regular />} />
            </MenuTrigger>

            <MenuPopover>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    setSelectedAppPerm(item)
                    setEditAppPermDrawerOpen(true)
                  }}
                >
                  Edit permissions
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setSelectedAppPerm(item)
                    setDeleteAppPermDrawerOpen(true)
                  }}
                >
                  Delete permissions
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )
      },
    }),
  ]

  return (
    <div className="container">
      <DataGridComponent
        items={applicationPerms}
        columns={columns}
        heightAdjustment={140}
        onAdd={() => setAddAppPermDrawerOpen(true)}
      />
      <OverlayDrawerComponent
        key="addform"
        open={addAppPermDrawerOpen}
        setOpen={setAddAppPermDrawerOpen}
        title="Add app permissions"
        content={
          <ApplicationPermissionsForm
            applicationId={props.appId}
            unavailableOptions={applicationPerms.map((x) => {
              return { roleId: x.roleId!, ftOrDaKey: x.resource == 'assets' ? `da_${x.id}` : `ft_${x.id}` }
            })}
            onSubmit={() => {
              setAddAppPermDrawerOpen(false)
              fetchPermsList()
            }}
          />
        }
      />
      <OverlayDrawerComponent
        key="editform"
        open={editAppPermDrawerOpen}
        setOpen={setEditAppPermDrawerOpen}
        title="Edit application permissions"
        content={
          <ApplicationPermissionsForm
            edit
            applicationId={props.appId}
            onSubmit={() => {
              setEditAppPermDrawerOpen(false)
              fetchPermsList()
            }}
            selectedAppPerm={selectedAppPerm!}
          />
        }
      />

      <OverlayDrawerComponent
        key="deleteform"
        open={deleteAppPermDrawerOpen}
        setOpen={setDeleteAppPermDrawerOpen}
        title="Delete application permissions"
        content={
          <ApplicationPermissionsForm
            delete
            applicationId={props.appId}
            onSubmit={() => {
              setEditAppPermDrawerOpen(false)
              fetchPermsList()
            }}
            onDelete={() => {
              setDeleteAppPermDrawerOpen(false)
              fetchPermsList()
            }}
            selectedAppPerm={selectedAppPerm!}
          />
        }
      />
    </div>
  )
}
